import type { ReactNode } from "react";
import { useRef, useState } from "react";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import type { TableServerQuery } from "@freightsimple/ui-core/Table/Table";
import type { ListWebhookDeliveriesResponse } from "@freightsimple/generated-dashboard-openapi-client/models/ListWebhookDeliveriesResponse";
import type { WebhookDeliveryDetail } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookDeliveryDetail";
import type { WebhookSummary } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookSummary";
import { ResponseError } from "@freightsimple/generated-dashboard-openapi-client/runtime";
import { SortDirection } from "@freightsimple/generated-dashboard-openapi-client/models/SortDirection";
import { WebhookDeliverySortKey } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookDeliverySortKey";
import { WebhookDeliveryState } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookDeliveryState";
import type { UUID } from "@/Shared/types";
import { useOnce } from "@/Hooks/useOnce";
import { useWebhooksApi } from "@/Hooks/useWebhooksApi";
import { WebhooksDeliveryDialog } from "../Dialogs/WebhooksDeliveryDialog";
import { addDays, toJsDate } from "@freightsimple/time/helpers";
import {
  useWebhooksDeliveriesFilters,
  WEBHOOKS_FILTER_ANY,
} from "./useWebhooksDeliveriesFilters";

interface Notice {
  title: string;
  message: string;
}

const GENERIC_FAILURE: Notice = {
  title: "Something went wrong",
  message: "Please try again, or contact FreightSimple if it keeps happening.",
};

const sortKeys = new Set<string>(Object.values(WebhookDeliverySortKey));

/** The table's column keys are the API's sort keys, so nothing else needs to map them. */
function asSortKey(key: string | undefined): WebhookDeliverySortKey {
  return key !== undefined && sortKeys.has(key)
    ? (key as WebhookDeliverySortKey)
    : WebhookDeliverySortKey.CreatedAt;
}

/**
 * Owns the Webhook deliveries screen's data and actions: the webhooks (for the filter and the
 * empty state), the current page of deliveries, the detail view and the redeliver. The filters
 * live in the URL; the page, size and sort live in the table, which reports them here through
 * `onQueryChange`, and every change refetches. The dialogs are declarative and live at screen
 * level: a row-owned dialog would be destroyed by the refetch that follows a redeliver.
 */
export function useWebhooksDeliveriesScreen() {
  const api = useWebhooksApi();
  const filters = useWebhooksDeliveriesFilters();
  const [webhooks, setWebhooks] = useState<WebhookSummary[] | undefined>();
  // Deliveries outlive a deleted webhook, so "nothing to show" needs both to be empty.
  const [hasRecentDeliveries, setHasRecentDeliveries] = useState(false);
  // A failed first load must not leave the page on its spinner forever; the notice says what
  // happened and the table renders empty until a reload.
  const [webhooksLoadFailed, setWebhooksLoadFailed] = useState(false);
  const [page, setPage] = useState<ListWebhookDeliveriesResponse | undefined>();
  const [query, setQuery] = useState<TableServerQuery | undefined>();
  const [loadingPage, setLoadingPage] = useState(false);
  // Two fetches can be in flight at once (Next clicked twice, or a page change
  // during the refresh after a redeliver); only the latest may set the page,
  // or page N's rows land under page N+1's footer.
  const latestPageRequest = useRef(0);
  const [viewTarget, setViewTarget] = useState<UUID | undefined>();
  const [viewDetail, setViewDetail] = useState<
    WebhookDeliveryDetail | undefined
  >();
  const [retryingDeliveryId, setRetryingDeliveryId] = useState<
    UUID | undefined
  >();
  const [notice, setNotice] = useState<Notice | undefined>();

  useOnce(async () => {
    try {
      const response = await api.listCompanyWebhooks();
      setWebhooks(response.webhooks);
      setHasRecentDeliveries(response.hasRecentDeliveries);
    } catch (e) {
      console.error(e);
      setWebhooksLoadFailed(true);
      setNotice(GENERIC_FAILURE);
    }
  });

  async function loadPage(tableQuery: TableServerQuery) {
    const requestNumber = ++latestPageRequest.current;
    setLoadingPage(true);
    try {
      const { filter } = filters;
      // The customer picks calendar days in their own timezone, so "to" the 15th means
      // everything before the 16th starts, locally.
      const createdFrom = filter.from ? toJsDate(filter.from) : undefined;
      const createdTo = filter.to ? toJsDate(addDays(filter.to, 1)) : undefined;
      // The generated request type has no room for an explicit undefined, so an unset filter
      // is left out rather than sent as one.
      const response = await api.listWebhookDeliveries({
        ...(filter.webhookId && { webhookId: filter.webhookId }),
        ...(filter.eventType !== WEBHOOKS_FILTER_ANY && {
          eventType: filter.eventType,
        }),
        ...(filter.state !== WEBHOOKS_FILTER_ANY && { state: filter.state }),
        ...(createdFrom && { createdFrom }),
        ...(createdTo && { createdTo }),
        sortBy: asSortKey(tableQuery.sortKey),
        sortDirection:
          tableQuery.sortDir === "asc" ? SortDirection.Asc : SortDirection.Desc,
        page: tableQuery.page,
        pageSize: tableQuery.pageSize,
      });
      if (requestNumber === latestPageRequest.current) setPage(response);
    } catch (e) {
      console.error(e);
      setNotice(GENERIC_FAILURE);
    } finally {
      if (requestNumber === latestPageRequest.current) setLoadingPage(false);
    }
  }

  /** The table calls this on mount and on every page, size or sort change. */
  function onQueryChange(tableQuery: TableServerQuery) {
    setQuery(tableQuery);
    loadPage(tableQuery);
  }

  // Same guard as the page: a second Details click before the first answers must not put the
  // first delivery's bodies under the second's title.
  const latestViewTarget = useRef<UUID | undefined>(undefined);
  async function onView(deliveryId: UUID) {
    latestViewTarget.current = deliveryId;
    setViewTarget(deliveryId);
    setViewDetail(undefined);
    try {
      const detail = await api.getWebhookDelivery({ deliveryId });
      if (latestViewTarget.current === deliveryId) setViewDetail(detail);
    } catch (e) {
      console.error(e);
      if (latestViewTarget.current !== deliveryId) return;
      setViewTarget(undefined);
      setNotice(GENERIC_FAILURE);
    }
  }

  async function onRetry(deliveryId: UUID) {
    setRetryingDeliveryId(deliveryId);
    try {
      const result = await api.retryWebhookDelivery({
        retryWebhookDeliveryRequest: { deliveryId },
      });
      if (result.state === WebhookDeliveryState.Delivered) {
        setNotice({
          title: "Delivered",
          message: "Your endpoint accepted the event.",
        });
      } else {
        const reason =
          result.httpStatus !== undefined
            ? `Your endpoint answered HTTP ${result.httpStatus}. Open the delivery details to see the response.`
            : (result.errorMessage ?? "Your endpoint could not be reached.");
        // A failed redelivery goes on the same retry schedule as any delivery, so it isn't over.
        // Said first: the reason can be a raw exception message with no closing period.
        setNotice({
          title: "Still failing",
          message:
            result.state === WebhookDeliveryState.Pending
              ? `We'll keep retrying it for about a day. ${reason}`
              : reason,
        });
      }
    } catch (e) {
      console.error(e);
      // A 400 or 409 carries a sentence written for the customer: the cap, a disabled webhook,
      // a delivery too old to re-send.
      if (
        e instanceof ResponseError &&
        (e.response.status === 400 || e.response.status === 409)
      ) {
        setNotice({
          title:
            e.response.status === 409
              ? "Redelivery limit reached"
              : "Can't re-send this delivery",
          message: await e.response.text(),
        });
      } else {
        setNotice(GENERIC_FAILURE);
      }
    } finally {
      setRetryingDeliveryId(undefined);
      // The new delivery is a row of its own and the root's count moved, so the page is stale
      // either way. A background refresh keeps the notice and any open dialog mounted.
      if (query) await loadPage(query);
    }
  }

  const dialogs: ReactNode = (
    <>
      <WebhooksDeliveryDialog
        open={viewTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setViewTarget(undefined);
            setViewDetail(undefined);
          }
        }}
        detail={viewDetail}
      />
      <Dialog
        open={notice !== undefined}
        onOpenChange={(open) => {
          if (!open) setNotice(undefined);
        }}
        title={notice?.title}
        footer={
          <Button primary onClick={() => setNotice(undefined)}>
            OK
          </Button>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          {notice?.message}
        </ControlPresentationProvider>
      </Dialog>
    </>
  );

  return {
    webhooks,
    hasRecentDeliveries,
    webhooksLoadFailed,
    page,
    loadingPage,
    filters,
    retryingDeliveryId,
    onQueryChange,
    onView,
    onRetry,
    dialogs,
  };
}
